import { CommonModule } from "@angular/common";
import { Component, computed, signal } from "@angular/core";
import { ColDef, GridOptions } from "ag-grid-community";

// EZ
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzBaseComponent } from "@template-ui/components";
import { DocMeta } from "@template-ui/decorators";
import {
  CrudInlineSaveCancelButtonDisplay,
  EzFormInputType,
  EzGridColTypes,
  EzGridDsConfig,
  InputConfig,
  PanelHeaderConfig
} from "@template-ui/types";

type StarWarsActor = {
  id: string;
  firstName: string;
  lastName: string;
  biography: string;
  trilogy: string;
  isLiving: boolean;
};

type SampleCreateButtonLocation = "none" | "belowGrid" | "panelHeader";

@DocMeta({
  hide: true,
  category: "Components",
  showcaseTitle: "Edit Mode Variants",
  showcaseDesc: "Demonstrates `dialog`, `inline-auto`, and `inline-button` edit modes.",
})
@Component({
  selector: "ui-grid-edit-mode-variants-sample, ez-grid-edit-mode-variants-sample",
  standalone: true,
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./ez-grid-hybrid-batch-edit-sample.component.html",
  styleUrl: "./ez-grid-hybrid-batch-edit-sample.component.scss",
})
export class EzGridEditModeVariantsSampleComponent extends EzBaseComponent {
  protected readonly modeFormValues = signal({
    editMode: "inline-button",
    createButtonLocation: "none" as SampleCreateButtonLocation,
    allowDelete: false,
    saveCancelButtonDisplay: "text" as CrudInlineSaveCancelButtonDisplay,
  });

  protected readonly modeFormConfig: InputConfig[] = [
    {
      type: EzFormInputType.SELECT,
      name: "editMode",
      label: "Edit Mode",
      width: "1x",
      options: [
        { viewValue: "Dialog", value: "dialog" },
        { viewValue: "Inline Auto", value: "inline-auto" },
        { viewValue: "Inline Buttons", value: "inline-button" },
      ],
    },
    {
      type: EzFormInputType.SELECT,
      name: "createButtonLocation",
      label: "Add Button",
      width: "1x",
      options: [
        { viewValue: "None", value: "none" },
        { viewValue: "Below Grid", value: "belowGrid" },
        { viewValue: "Panel Header", value: "panelHeader" },
      ],
    },
    {
      type: EzFormInputType.CHECKBOX,
      name: "allowDelete",
      label: "Allow Delete",
      width: "1x",
    },
    {
      type: EzFormInputType.SELECT,
      name: "saveCancelButtonDisplay",
      label: "Inline Save/Cancel",
      width: "1x",
      options: [
        { viewValue: "Text", value: "text" },
        { viewValue: "Icon", value: "icon" },
        { viewValue: "Text + Icon", value: "text+icon" },
      ],
      disabled: computed(() => this.modeFormValues().editMode !== "inline-button"),
    },
  ];

  protected readonly agGridOptions: GridOptions = {
    paginationPageSize: 5,
  };

  protected readonly panelHeaderConfig = computed<PanelHeaderConfig>(() => ({
    panelHeaderTitle: "Edit Mode Variants Sample",
    subtitle: this.buildSubtitle(),
    showReloadButton: true,
    hideExportToExcel: true,
  }));

  protected readonly colDefs: ColDef<StarWarsActor>[] = [
    { field: "id", maxWidth: 120 },
    { field: "firstName", headerName: "First Name", type: [EzGridColTypes.EditText] },
    { field: "lastName", headerName: "Last Name", type: [EzGridColTypes.EditText] },
    {
      field: "biography",
      headerName: "Biography",
      minWidth: 360,
      type: [EzGridColTypes.EditTextArea],
    },
    { field: "trilogy", headerName: "Trilogy", maxWidth: 140, type: [EzGridColTypes.EditSelect], cellEditorParams: { values: ["original", "prequel", "sequel", "expanded"] } },
    { field: "isLiving", headerName: "Is Living", maxWidth: 120, type: [EzGridColTypes.EditCheckbox] },
  ];

  protected readonly editDialogInputs: InputConfig[] = [
    { type: EzFormInputType.TEXT, name: "firstName", label: "First Name", width: "1x" },
    { type: EzFormInputType.TEXT, name: "lastName", label: "Last Name", width: "1x" },
    { type: EzFormInputType.TEXTAREA, name: "biography", label: "Biography", width: "fill", textAreaRows: 8, maxLength: 4000 },
    { type: EzFormInputType.SELECT, name: "trilogy", label: "Trilogy", width: "1x", options: [
      { viewValue: "original", value: "original" },
      { viewValue: "prequel", value: "prequel" },
      { viewValue: "sequel", value: "sequel" },
      { viewValue: "expanded", value: "expanded" },
    ]},
  ];

  protected readonly dsConfig = computed<EzGridDsConfig>(() => ({
    datasourceId: "EZ_GRID_HYBRID_BATCH_SAMPLE",
    colDefs: this.colDefs,
    bindings: [
      { bindingType: "CREATE", endpoint: "mock/StarWarsActors", protocol: "POST_JSON" },
      { bindingType: "READ", endpoint: "mock/StarWarsActors", protocol: "GET" },
      { bindingType: "UPDATE", endpoint: "mock/StarWarsActors/{id}", protocol: "PUT_JSON" },
      { bindingType: "DELETE", endpoint: "mock/StarWarsActors/{id}", protocol: "DELETE" },
    ],
    crudActions: {
      canEdit: true,
      canCreate: this.modeFormValues().createButtonLocation !== "none",
      canDelete: this.modeFormValues().allowDelete,
      idField: "id",
      editMode: this.modeFormValues().editMode as "dialog" | "inline-auto" | "inline-button",
      actions: {
        create: {
          autoButton: {
            buttonText: "Add Actor",
            location: this.resolveCreateButtonLocation(),
          },
        },
      },
      formConfig: {
        inputConfig: this.editDialogInputs,
        dialogData: { header: "Edit Actor" },
      },
      inlineConfig: {
        saveCancelButtonDisplay: this.modeFormValues().saveCancelButtonDisplay,
        validateChanges: (_row, changes) => {
          const firstName = changes["firstName"];
          if (firstName !== undefined && !String(firstName).trim()) {
            return "First Name is required.";
          }
          return undefined;
        },
      },
    },
  }));

  public constructor() {
    super();
    this.thisClassName = "EzGridEditModeVariantsSampleComponent";
  }

  private buildSubtitle(): string {
    const { allowDelete, createButtonLocation, editMode, saveCancelButtonDisplay } = this.modeFormValues();
    const inlineButtonSummary = editMode === "inline-button"
      ? ` | Buttons: ${this.formatInlineButtonDisplay(saveCancelButtonDisplay)}`
      : "";

    return `Mode: ${editMode} | Add: ${this.formatCreateButtonLocation(createButtonLocation)} | Delete: ${allowDelete ? "On" : "Off"}${inlineButtonSummary}`;
  }

  private formatInlineButtonDisplay(display: CrudInlineSaveCancelButtonDisplay): string {
    switch (display) {
      case "icon":
        return "Icon";
      case "text+icon":
        return "Text + Icon";
      default:
        return "Text";
    }
  }

  private formatCreateButtonLocation(location: SampleCreateButtonLocation): string {
    switch (location) {
      case "belowGrid":
        return "Below Grid";
      case "panelHeader":
        return "Panel Header";
      default:
        return "None";
    }
  }

  private resolveCreateButtonLocation(): "belowGrid" | "panelHeader" {
    return this.modeFormValues().createButtonLocation === "panelHeader" ? "panelHeader" : "belowGrid";
  }
}
